import {
    useEffect,
    useMemo,
    useReducer,
    useRef,
} from 'react';
import { useViewerBinaryCache } from '../../viewer-cache';
import {
    buildInitialSceneAssetState,
    createTextureBlobFromResource,
    getSceneAssetResourcePlan,
    patchSceneAssetState,
} from './sceneAssetResources';

function releaseSceneAssetHandle(resourceHandlesRef, modelId, resourceType) {
    const modelHandles = resourceHandlesRef.current.get(modelId);

    if (!modelHandles?.[resourceType]) {
        return;
    }

    modelHandles[resourceType].release();
    delete modelHandles[resourceType];

    if (!modelHandles.geometry && !modelHandles.texture) {
        resourceHandlesRef.current.delete(modelId);
    }
}

function releaseAllSceneAssetHandles(resourceHandlesRef) {
    resourceHandlesRef.current.forEach((modelHandles, modelId) => {
        if (modelHandles.geometry) {
            releaseSceneAssetHandle(resourceHandlesRef, modelId, 'geometry');
        }

        if (modelHandles.texture) {
            releaseSceneAssetHandle(resourceHandlesRef, modelId, 'texture');
        }
    });
}

function ensureSceneAssetHandles(resourceHandlesRef, modelId) {
    const existingHandles = resourceHandlesRef.current.get(modelId);

    if (existingHandles) {
        return existingHandles;
    }

    const nextHandles = {};
    resourceHandlesRef.current.set(modelId, nextHandles);
    return nextHandles;
}

function reconcileActiveSceneAssets(currentAssetsById, activeSceneModels) {
    const currentModelIds = Object.keys(currentAssetsById);
    const nextAssetsById = {};
    let hasChanges = currentModelIds.length !== activeSceneModels.length;

    activeSceneModels.forEach((model) => {
        const currentState = currentAssetsById[model.id];

        if (currentState && currentState.descriptorKey === model.descriptorKey) {
            const nextState = currentState.model === model
                ? currentState
                : {
                    ...currentState,
                    model,
                };
            nextAssetsById[model.id] = nextState;

            if (nextState !== currentState) {
                hasChanges = true;
            }
            return;
        }

        nextAssetsById[model.id] = buildInitialSceneAssetState(model);
        hasChanges = true;
    });

    return hasChanges ? nextAssetsById : currentAssetsById;
}

function patchSceneAssetEntry(currentAssetsById, modelId, descriptorKey, patch) {
    const currentState = currentAssetsById[modelId];

    if (!currentState || currentState.descriptorKey !== descriptorKey) {
        return currentAssetsById;
    }

    return {
        ...currentAssetsById,
        [modelId]: patchSceneAssetState(currentState, patch),
    };
}

function createLoadingPatch(resourceType) {
    return resourceType === 'geometry'
        ? {
            error: null,
            geometryBuffer: null,
            geometryStatus: 'loading',
        }
        : {
            error: null,
            textureBlob: null,
            textureStatus: 'loading',
        };
}

function createReadyPatch(resourceType, resource) {
    return resourceType === 'geometry'
        ? {
            error: null,
            geometryBuffer: resource.data,
            geometryStatus: 'ready',
        }
        : {
            error: null,
            textureBlob: createTextureBlobFromResource(resource),
            textureStatus: 'ready',
        };
}

function createFailurePatch(resourceType, error) {
    return resourceType === 'geometry'
        ? {
            error,
            geometryStatus: 'failed',
        }
        : {
            error,
            textureStatus: 'failed',
        };
}

function sceneAssetCollectionReducer(currentAssetsById, action) {
    switch (action.type) {
        case 'reset':
            return Object.keys(currentAssetsById).length === 0 ? currentAssetsById : {};
        case 'reconcile-active-models':
            return reconcileActiveSceneAssets(currentAssetsById, action.activeSceneModels);
        case 'patch-asset-state':
            return patchSceneAssetEntry(
                currentAssetsById,
                action.modelId,
                action.descriptorKey,
                action.patch,
            );
        default:
            return currentAssetsById;
    }
}

export function useSceneAssetCollection({
    activeModelIds,
    enabled = true,
    resourceScopeId = null,
    sceneModels,
}) {
    const viewerBinaryCache = useViewerBinaryCache();
    const [assetsById, dispatch] = useReducer(sceneAssetCollectionReducer, {});
    const resourceHandlesRef = useRef(new Map());
    const activeModelIdSet = useMemo(
        () => new Set(activeModelIds),
        [activeModelIds],
    );
    const activeSceneModels = useMemo(
        () => sceneModels.filter((model) => activeModelIdSet.has(model.id)),
        [activeModelIdSet, sceneModels],
    );
    const activeSceneResourcePlansByModelId = useMemo(
        () => new Map(activeSceneModels.map((model) => [
            model.id,
            getSceneAssetResourcePlan(model),
        ])),
        [activeSceneModels],
    );

    useEffect(() => {
        if (!enabled || activeSceneModels.length === 0) {
            releaseAllSceneAssetHandles(resourceHandlesRef);
            dispatch({
                type: 'reset',
            });
            return undefined;
        }

        resourceHandlesRef.current.forEach((modelHandles, modelId) => {
            const activeResourcePlan = activeSceneResourcePlansByModelId.get(modelId);

            if (!activeResourcePlan) {
                if (modelHandles.geometry) {
                    releaseSceneAssetHandle(resourceHandlesRef, modelId, 'geometry');
                }

                if (modelHandles.texture) {
                    releaseSceneAssetHandle(resourceHandlesRef, modelId, 'texture');
                }

                return;
            }

            const expectedGeometryCacheKey = activeResourcePlan.geometry?.cacheKey || null;
            const expectedTextureCacheKey = activeResourcePlan.texture?.cacheKey || null;

            if (
                modelHandles.geometry
                && (
                    !expectedGeometryCacheKey
                    || modelHandles.geometry.cacheKey !== expectedGeometryCacheKey
                    || modelHandles.geometry.descriptorKey !== activeResourcePlan.descriptorKey
                )
            ) {
                releaseSceneAssetHandle(resourceHandlesRef, modelId, 'geometry');
            }

            if (
                modelHandles.texture
                && (
                    !expectedTextureCacheKey
                    || modelHandles.texture.cacheKey !== expectedTextureCacheKey
                    || modelHandles.texture.descriptorKey !== activeResourcePlan.descriptorKey
                )
            ) {
                releaseSceneAssetHandle(resourceHandlesRef, modelId, 'texture');
            }
        });

        dispatch({
            activeSceneModels,
            type: 'reconcile-active-models',
        });

        const acquireSceneAssetResource = ({
            model,
            resourcePlan,
        }) => {
            if (!resourcePlan) {
                return;
            }

            const {
                cacheKey,
                resourceType,
                type,
                url,
            } = resourcePlan;
            const modelHandles = ensureSceneAssetHandles(resourceHandlesRef, model.id);
            const currentHandle = modelHandles[resourceType];

            if (
                currentHandle
                && currentHandle.cacheKey === cacheKey
                && currentHandle.descriptorKey === model.descriptorKey
            ) {
                return;
            }

            if (currentHandle) {
                releaseSceneAssetHandle(resourceHandlesRef, model.id, resourceType);
            }

            dispatch({
                descriptorKey: model.descriptorKey,
                modelId: model.id,
                patch: createLoadingPatch(resourceType),
                type: 'patch-asset-state',
            });

            const acquiredResource = viewerBinaryCache.acquire({
                consumerId: `${model.id}:${resourceType}`,
                scopeId: resourceScopeId ?? undefined,
                type,
                url,
            });

            modelHandles[resourceType] = {
                cacheKey,
                descriptorKey: model.descriptorKey,
                release: acquiredResource.release,
            };

            acquiredResource.promise
                .then((resource) => {
                    const activeHandle = resourceHandlesRef.current.get(model.id)?.[resourceType];

                    if (
                        !activeHandle
                        || activeHandle.cacheKey !== cacheKey
                        || activeHandle.descriptorKey !== model.descriptorKey
                    ) {
                        return;
                    }

                    dispatch({
                        descriptorKey: model.descriptorKey,
                        modelId: model.id,
                        patch: createReadyPatch(resourceType, resource),
                        type: 'patch-asset-state',
                    });
                })
                .catch((error) => {
                    const activeHandle = resourceHandlesRef.current.get(model.id)?.[resourceType];

                    if (
                        !activeHandle
                        || activeHandle.cacheKey !== cacheKey
                        || activeHandle.descriptorKey !== model.descriptorKey
                    ) {
                        return;
                    }

                    if (viewerBinaryCache.isAbortError(error)) {
                        return;
                    }

                    dispatch({
                        descriptorKey: model.descriptorKey,
                        modelId: model.id,
                        patch: createFailurePatch(resourceType, error),
                        type: 'patch-asset-state',
                    });
                });
        };

        activeSceneModels.forEach((model) => {
            const resourcePlan = activeSceneResourcePlansByModelId.get(model.id);

            acquireSceneAssetResource({
                model,
                resourcePlan: resourcePlan.geometry,
            });

            if (resourcePlan.texture) {
                acquireSceneAssetResource({
                    model,
                    resourcePlan: resourcePlan.texture,
                });
                return;
            }

            releaseSceneAssetHandle(resourceHandlesRef, model.id, 'texture');
        });

        return undefined;
    }, [
        activeSceneModels,
        activeSceneResourcePlansByModelId,
        enabled,
        resourceScopeId,
        viewerBinaryCache,
    ]);

    useEffect(() => () => {
        releaseAllSceneAssetHandles(resourceHandlesRef);
    }, []);

    return {
        assetsById,
    };
}
